import { useMemo, useState, useEffect } from 'react';
import {
  Card,
  Table,
  Typography,
  Tag,
  Progress,
  Select,
  Row,
  Col,
  Statistic,
  Button,
  Space,
  DatePicker,
  Alert,
  theme
} from 'antd';
import {
  WarningOutlined,
  ReloadOutlined,
  ToolOutlined,
  LineChartOutlined,
  ScheduleOutlined,
  AlertOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useNavigate, useLocation } from 'react-router-dom';
import { trpc } from '../lib/api';
import { PageHeader, PageContainer } from '../components/common';
import { hasRequiredHealthParameters } from './utils/healthParameterVisibility';
import {
  createCategoryLabelMap,
  createLocationNameMap,
  PREDICTION_DATA_REQUIRED_LABEL,
  PREDICTION_DATA_REQUIRED_MESSAGE,
  resolveCategoryLabel,
  resolveLocationName,
  resolveMaintenanceRecommendation,
} from './utils/deteriorationListDisplay';
import { sortDeteriorationRows } from './utils/deteriorationListSorting';
import {
  DEFAULT_DETERIORATION_LIST_SORT,
  DETERIORATION_LIST_SORT_STORAGE_KEY,
  getSortOrderLabel,
  loadSortPreference,
  persistSortPreference,
  type DeteriorationListSortField,
  type SortPreference,
} from './utils/sortPreferences';

const { RangePicker } = DatePicker;

interface DeteriorationData {
  id: string;
  equipmentDisplayId: string;
  equipmentName: string;
  location: string;
  category: string;
  currentRate: number | null;  // null = 計算に必要なパラメータ不足
  predictedRate30Days: number | null;
  predictedRate90Days: number | null;
  predictedRate365Days: number | null;
  importanceLevel: number; // 重要度: 1:低, 2:中, 3:高, 4:最重要
  maintenanceRecommendation: string;
  estimatedFailureDate: string;
}

type EquipmentSummary = {
  id: string;
  displayId?: string | null;
  name: string;
  category?: unknown;
  categoryInfo?: unknown;
  categoryLabel?: string | null;
  categoryKey?: string | null;
  location?: unknown;
  locationName?: string | null;
  locationId?: string | null;
  equipmentCategoryId?: string | null;
  maintenanceRecommendation?: string | null;
  currentDeteriorationRate?: string | number | null;
  dpEul?: string | number | null;
  dpBeta?: string | number | null;
  manufacturerLifespan?: string | number | null;
  commissionedDate?: string | Date | null;
  installationDate?: string | Date | null;
  startDate?: string | Date | null;
};

type PredictionYear = {
  year?: number | string;
  rate?: number | string;
  maintenancePriority?: string;
  maintenanceRecommendation?: string;
};

type LatestPredictionSummary = {
  predictedRate?: number | string | null;
  remainingLife?: number | string | null;
  maintenanceRecommendation?: string | null;
  predictionsYearly?: PredictionYear[] | null;
};

type LocationTreeNode = {
  id?: string | null;
  name?: string | null;
  children?: LocationTreeNode[] | null;
};

type EquipmentCategorySummary = {
  id?: string | null;
  categoryLabel?: string | null;
};

type RiskGroupEntry =
  | EquipmentSummary
  | {
    equipment: EquipmentSummary;
    latestPrediction?: LatestPredictionSummary | null;
  };

type RiskGroupsResponse = {
  critical: RiskGroupEntry[];
  high: RiskGroupEntry[];
  medium: RiskGroupEntry[];
  low: RiskGroupEntry[];
};

type HealthFilterKey =
  | 'all'
  | 'excellent'
  | 'good'
  | 'warning'
  | 'critical'
  | '70below'
  | '50below';

const toNumber = (value: string | number | null | undefined, fallback = 0): number => {
  if (value === null || value === undefined) {
    return fallback;
  }
  const numeric = typeof value === 'number' ? value : Number(value);
  return Number.isNaN(numeric) ? fallback : numeric;
};

// null を保持するバージョン - 計算不可を明示
const toNullableNumber = (value: string | number | null | undefined): number | null => {
  if (value === null || value === undefined) {
    return null;
  }
  const numeric = typeof value === 'number' ? value : Number(value);
  return Number.isNaN(numeric) ? null : numeric;
};

const normalizePredictionYear = (value: unknown): PredictionYear => {
  if (value && typeof value === 'object') {
    const candidate = value as {
      year?: unknown;
      rate?: unknown;
      maintenance_priority?: unknown;
      maintenancePriority?: unknown;
      maintenance_recommendation?: unknown;
      maintenanceRecommendation?: unknown;
    };
    const year =
      typeof candidate.year === 'number' || typeof candidate.year === 'string'
        ? candidate.year
        : undefined;
    const rate =
      typeof candidate.rate === 'number' || typeof candidate.rate === 'string'
        ? candidate.rate
        : undefined;
    const maintenancePriority =
      typeof candidate.maintenance_priority === 'string'
        ? candidate.maintenance_priority
        : typeof candidate.maintenancePriority === 'string'
          ? candidate.maintenancePriority
          : undefined;
    const maintenanceRecommendation =
      typeof candidate.maintenance_recommendation === 'string'
        ? candidate.maintenance_recommendation
        : typeof candidate.maintenanceRecommendation === 'string'
          ? candidate.maintenanceRecommendation
          : undefined;
    return { year, rate, maintenancePriority, maintenanceRecommendation };
  }
  return {};
};

const getErrorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof Error) {
    return error.message || fallback;
  }
  if (error && typeof error === 'object' && 'message' in error) {
    const message = (error as { message: unknown }).message;
    if (typeof message === 'string' && message.trim().length > 0) {
      return message;
    }
  }
  return fallback;
};

const normalizeHealthFilter = (rawValue: string | null | undefined): HealthFilterKey => {
  switch (rawValue) {
    case 'excellent':
    case 'good':
    case 'warning':
    case 'critical':
    case '70below':
    case '50below':
      return rawValue;
    // 旧キー（劣化率ベース）との互換
    case '30plus':
      return '70below';
    case '50plus':
      return '50below';
    // Dashboard KPI 由来の誤キーを吸収
    case '70above':
      return '70below';
    default:
      return 'all';
  }
};

const toHealthIndex = (deteriorationRate: number): number => 100 - deteriorationRate;

const matchesHealthFilter = (
  filter: HealthFilterKey,
  deteriorationRate: number | null,
): boolean => {
  if (filter === 'all') {
    return true;
  }
  if (deteriorationRate === null) {
    return false;
  }

  const healthIndex = toHealthIndex(deteriorationRate);
  switch (filter) {
    case 'excellent':
      return healthIndex >= 70;
    case 'good':
      return healthIndex >= 50 && healthIndex < 70;
    case 'warning':
      return healthIndex >= 30 && healthIndex < 50;
    case 'critical':
      return healthIndex < 30;
    case '70below':
      return healthIndex <= 70;
    case '50below':
      return healthIndex <= 50;
    default:
      return true;
  }
};

export function DeteriorationPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = theme.useToken();
  const [selectedLocation, setSelectedLocation] = useState<string>('all');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedRiskLevel, setSelectedRiskLevel] = useState<string>('all');
  const [selectedDeteriorationRate, setSelectedDeteriorationRate] = useState<HealthFilterKey>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [sortPreference, setSortPreference] = useState<SortPreference<DeteriorationListSortField>>(() =>
    loadSortPreference(
      DETERIORATION_LIST_SORT_STORAGE_KEY,
      ['equipmentName', 'health', 'importanceLevel'] as const,
      DEFAULT_DETERIORATION_LIST_SORT,
    )
  );

  useEffect(() => {
    persistSortPreference(DETERIORATION_LIST_SORT_STORAGE_KEY, sortPreference);
  }, [sortPreference]);

  // URLのstateから健全度フィルターを設定
  useEffect(() => {
    const state = (location.state ?? null) as {
      healthIndex?: string;
      deteriorationRate?: string;
      importanceLevel?: string;
    } | null;

    // 新形式（健全度ベース）
    if (state?.healthIndex) {
      setSelectedDeteriorationRate(normalizeHealthFilter(state.healthIndex));
    }
    // 旧形式の互換性（劣化率ベース）
    else if (state?.deteriorationRate) {
      setSelectedDeteriorationRate(normalizeHealthFilter(state.deteriorationRate));
    }
    // 古い形式の互換性
    if (state?.importanceLevel) {
      setSelectedRiskLevel(state.importanceLevel);
    }
  }, [location.state]);

  useEffect(() => {
    setCurrentPage(1);
  }, [
    selectedLocation,
    selectedCategory,
    selectedRiskLevel,
    selectedDeteriorationRate,
    sortPreference,
  ]);

  // 設備一覧を取得（設備0件の場合の誘導表示に使用）
  const {
    data: equipmentsData,
    isLoading: isLoadingEquipments
  } = trpc.equipment.list.useQuery({ page: 1, limit: 1 });

  const hasEquipments = (equipmentsData?.pagination?.total ?? 0) > 0;

  // APIから取得（健全度予測データがある設備のみ）
  const { data: riskGroupsResponse, isLoading, error } = trpc.deterioration.getByRiskLevel.useQuery({
    onlyWithPredictions: true
  });
  const riskGroups = riskGroupsResponse as RiskGroupsResponse | undefined;
  const { data: healthManagementMode } = trpc.company.getHealthManagementMode.useQuery();
  const { data: locationsTree } = trpc.location.getTree.useQuery();
  const { data: equipmentCategories } = trpc.equipmentCategory.list.useQuery({
    includeInactive: true,
  });

  const locationNameById = useMemo(() => {
    const locationNodes = Array.isArray(locationsTree) ? (locationsTree as LocationTreeNode[]) : [];
    return createLocationNameMap(locationNodes);
  }, [locationsTree]);

  const categoryLabelById = useMemo(() => {
    const categories = Array.isArray(equipmentCategories) ? (equipmentCategories as EquipmentCategorySummary[]) : [];
    return createCategoryLabelMap(categories);
  }, [equipmentCategories]);

  // グループをフラット化
  const deteriorationData: DeteriorationData[] = useMemo(() => {
    if (!riskGroups) {
      return [];
    }

    const mapGroup = (items: RiskGroupEntry[] | undefined, group: number): DeteriorationData[] => {
      if (!items || items.length === 0) {
        return [];
      }

      return items
        .map((entry) => {
          const equipment = ('equipment' in entry ? entry.equipment : entry) as EquipmentSummary | undefined;
          if (!equipment) {
            return null;
          }

          const prediction: LatestPredictionSummary | null =
            'latestPrediction' in entry ? entry.latestPrediction ?? null : null;
          const hasRequiredParams = hasRequiredHealthParameters(healthManagementMode, {
            dpEul: equipment.dpEul,
            dpBeta: equipment.dpBeta,
            manufacturerLifespan: equipment.manufacturerLifespan,
            commissionedDate: equipment.commissionedDate,
            installationDate: equipment.installationDate,
            startDate: equipment.startDate,
          });
          const hasPredictionData =
            hasRequiredParams &&
            !!prediction &&
            (
              (Array.isArray(prediction.predictionsYearly) && prediction.predictionsYearly.length > 0) ||
              prediction.predictedRate !== null &&
              prediction.predictedRate !== undefined
            );

          // null を保持して計算不可を明示（AVS-94）
          const currentRate = hasPredictionData
            ? toNullableNumber(equipment.currentDeteriorationRate)
            : null;

          const predictedRateRaw = prediction?.predictedRate;
          const predicted = hasPredictionData ? toNullableNumber(predictedRateRaw) : null;

          const predictionsYearly: PredictionYear[] =
            hasPredictionData && prediction?.predictionsYearly
              ? prediction.predictionsYearly.map(normalizePredictionYear)
              : [];
          const thisYear = new Date().getFullYear();
          const thisYearPrediction = predictionsYearly.find(
            (yearItem) => Number(yearItem?.year) === thisYear,
          );
          const pickYear = (offset: number): number | null => {
            if (!hasPredictionData) {
              return null;
            }
            const matching = predictionsYearly.find(
              (yearItem) => Number(yearItem?.year) === thisYear + offset
            );
            const rateValue = matching?.rate;
            // 予測がある場合はその値、なければ predicted（currentRate）にフォールバック
            return toNullableNumber(rateValue) ?? predicted ?? currentRate;
          };

          const rateOneYear = pickYear(1);
          const rateTwoYears = pickYear(2);
          const rateThreeYears = pickYear(3);

          const remainingLifeRaw = hasPredictionData ? prediction?.remainingLife : null;
          const remainingLifeValue =
            remainingLifeRaw !== null && remainingLifeRaw !== undefined
              ? toNumber(remainingLifeRaw, Number.NaN)
              : Number.NaN;
          const remainingLife = Number.isNaN(remainingLifeValue) ? null : remainingLifeValue;

          const estimatedFailureDate =
            typeof remainingLife === 'number'
              ? new Date(Date.now() + remainingLife * 365 * 24 * 60 * 60 * 1000)
                .toISOString()
                .split('T')[0]
              : '-';

          const equipmentRecord = equipment as Record<string, unknown>;
          const locationName = resolveLocationName({
            locationId: equipment.locationId,
            location: equipment.location ?? equipmentRecord.location,
            locationName: equipment.locationName ?? equipmentRecord.locationName,
            locationNameById,
          });
          const categoryLabel = resolveCategoryLabel({
            equipmentCategoryId: equipment.equipmentCategoryId,
            categoryInfo: equipment.categoryInfo ?? equipmentRecord.categoryInfo,
            categoryLabel: equipment.categoryLabel ?? equipmentRecord.categoryLabel,
            legacyCategory: equipment.category,
            categoryLabelById,
          });
          const maintenanceRecommendation = resolveMaintenanceRecommendation({
            currentRate,
            predictedRate: predicted,
            maintenancePriority: thisYearPrediction?.maintenancePriority,
            maintenanceRecommendation:
              thisYearPrediction?.maintenanceRecommendation ??
              prediction?.maintenanceRecommendation ??
              equipment.maintenanceRecommendation ??
              null,
          });

          return {
            id: equipment.id,
            equipmentDisplayId: equipment.displayId ?? '-',
            equipmentName: equipment.name ?? '-',
            location: locationName,
            category: categoryLabel,
            currentRate,
            predictedRate30Days: rateOneYear,
            predictedRate90Days: rateTwoYears,
            predictedRate365Days: rateThreeYears,
            importanceLevel: group,
            maintenanceRecommendation,
            estimatedFailureDate,
          };
        })
        .filter((entry): entry is DeteriorationData => entry !== null);
    };

    return [
      ...mapGroup(riskGroups?.critical, 4),
      ...mapGroup(riskGroups?.high, 3),
      ...mapGroup(riskGroups?.medium, 2),
      ...mapGroup(riskGroups?.low, 1),
    ];
  }, [riskGroups, healthManagementMode, locationNameById, categoryLabelById]);

  const getRiskColor = (level: number) => {
    switch (level) {
      case 1: return 'green'; // low
      case 2: return 'orange'; // medium
      case 3: return 'red'; // high
      case 4: return 'purple'; // critical
      default: return 'default';
    }
  };

  const getRiskText = (level: number) => {
    switch (level) {
      case 1: return '低'; // low
      case 2: return '中'; // medium
      case 3: return '高'; // high
      case 4: return '最重要'; // critical
      default: return '-';
    }
  };

  // 健全度ベースの色分け（高い値=緑、低い値=赤）
  const getHealthColor = (healthIndex: number) => {
    if (healthIndex >= 70) return '#52c41a';  // 緑（良好）
    if (healthIndex >= 50) return '#faad14';  // 黄（注意）
    if (healthIndex >= 30) return '#ff7a45';  // オレンジ（警告）
    return '#ff4d4f';  // 赤（危険）
  };

  const formatHealthPct = (rate: number | null) => {
    // パラメータ不足で計算不可の場合（AVS-94）
    if (rate === null) {
      return <span style={{ color: '#999' }}>???%</span>;
    }
    const healthIndex = 100 - rate;  // 劣化率→健全度に変換
    const value = `${Number(healthIndex).toFixed(1)}%`;
    if (healthIndex <= 50) {
      return <span style={{ color: '#ff4d4f', fontWeight: 'bold' }}>{value}</span>;
    }
    if (healthIndex <= 70) {
      return <span style={{ color: '#faad14', fontWeight: 'bold' }}>{value}</span>;
    }
    return value;
  };

  // 複雑Ver（ワイブル分布）専用のカラム定義
  const columns: ColumnsType<DeteriorationData> = [
    {
      title: '設備名',
      dataIndex: 'equipmentName',
      key: 'equipmentName',
      width: 180,
      fixed: 'left',
      render: (name: string, record: DeteriorationData) => (
        <a
          href={`/app/deterioration/${record.id}`}
          onClick={(e) => { e.preventDefault(); navigate(`/app/deterioration/${record.id}`); }}
          style={{ color: token.colorLink, textDecoration: 'none', cursor: 'pointer' }}
        >
          {name}
        </a>
      ),
    },
    {
      title: '設置場所',
      dataIndex: 'location',
      key: 'location',
      width: 120,
    },
    {
      title: 'カテゴリ',
      dataIndex: 'category',
      key: 'category',
      width: 100,
    },
    {
      title: '現在健全度',
      dataIndex: 'currentRate',
      key: 'currentRate',
      width: 120,
      render: (rate: number | null) => {
        // パラメータ不足で計算不可の場合（AVS-94）
        if (rate === null) {
          return <span style={{ color: '#999' }}>???%</span>;
        }
        const healthIndex = 100 - rate;  // 劣化率→健全度
        const textColor = healthIndex <= 50 ? '#ff4d4f' : healthIndex <= 70 ? '#faad14' : undefined;
        return (
          <Progress
            percent={healthIndex}
            size="small"
            strokeColor={getHealthColor(healthIndex)}
            format={(percent) => (
              <span style={{ color: textColor, fontWeight: textColor ? 'bold' : 'normal' }}>
                {percent?.toFixed(1)}%
              </span>
            )}
          />
        );
      },
    },
    {
      title: '予測健全度',
      children: [
        {
          title: '1年後',
          dataIndex: 'predictedRate30Days',
          key: 'predictedRate30Days',
          width: 80,
          render: (rate: number | null) => formatHealthPct(rate),
        },
        {
          title: '2年後',
          dataIndex: 'predictedRate90Days',
          key: 'predictedRate90Days',
          width: 80,
          render: (rate: number | null) => formatHealthPct(rate),
        },
        {
          title: '3年後',
          dataIndex: 'predictedRate365Days',
          key: 'predictedRate365Days',
          width: 80,
          render: (rate: number | null) => formatHealthPct(rate),
        },
      ],
    },
    {
      title: '重要度',
      dataIndex: 'importanceLevel',
      key: 'importanceLevel',
      width: 100,
      render: (level: number) => (
        <Tag color={getRiskColor(level)}>{getRiskText(level)}</Tag>
      ),
    },
    {
      title: '推奨対応',
      dataIndex: 'maintenanceRecommendation',
      key: 'maintenanceRecommendation',
      width: 200,
      render: (recommendation: string) =>
        recommendation === PREDICTION_DATA_REQUIRED_LABEL ? (
          <Typography.Text type="secondary">{recommendation}</Typography.Text>
        ) : (
          recommendation
        ),
    },
  ];

  const handleRefresh = () => {
    // TODO: implement prediction refresh
  };

  const locationOptions = useMemo(() => (
    Array.from(
      new Set(
        deteriorationData
          .map((item) => item.location)
          .filter((name) => typeof name === 'string' && name.trim().length > 0 && name !== '未設定')
      )
    ).sort((a, b) => a.localeCompare(b, 'ja'))
  ), [deteriorationData]);

  const categoryOptions = useMemo(() => (
    Array.from(
      new Set(
        deteriorationData
          .map((item) => item.category)
          .filter((name) => typeof name === 'string' && name.trim().length > 0 && name !== '未設定')
      )
    ).sort((a, b) => a.localeCompare(b, 'ja'))
  ), [deteriorationData]);

  // フィルタリング
  let filteredData = deteriorationData;
  if (selectedLocation !== 'all') {
    filteredData = filteredData.filter(item => item.location === selectedLocation);
  }
  if (selectedCategory !== 'all') {
    filteredData = filteredData.filter(item => item.category === selectedCategory);
  }
  if (selectedRiskLevel !== 'all') {
    const riskMap: Record<string, number> = { low: 1, medium: 2, high: 3, critical: 4 };
    const target = riskMap[selectedRiskLevel as keyof typeof riskMap];
    if (target) {
      filteredData = filteredData.filter(item => item.importanceLevel === target);
    }
  }
  if (selectedDeteriorationRate !== 'all') {
    filteredData = filteredData.filter((item) => matchesHealthFilter(selectedDeteriorationRate, item.currentRate));
  }

  const sortedData = useMemo(
    () => sortDeteriorationRows(filteredData, sortPreference),
    [filteredData, sortPreference],
  );

  const filteredDataWithRate = filteredData.filter(
    (item): item is DeteriorationData & { currentRate: number } => item.currentRate !== null
  );
  const predictionDataMissingCount = filteredData.filter(
    (item) => item.maintenanceRecommendation === PREDICTION_DATA_REQUIRED_LABEL
  ).length;

  // 統計計算（健全度ベース）
  const criticalCount = filteredDataWithRate.filter(item => matchesHealthFilter('50below', item.currentRate)).length;
  const belowSeventyCount = filteredDataWithRate.filter(item => matchesHealthFilter('70below', item.currentRate)).length;
  const averageHealthIndex = filteredDataWithRate.length > 0
    ? Math.round(filteredDataWithRate.reduce((sum, item) => sum + toHealthIndex(item.currentRate), 0) / filteredDataWithRate.length)
    : 0;

  return (
    <PageContainer>
      <PageHeader
        title="健全度予測"
        subtitle="設備の劣化状況を予測し、最適な保全タイミングを計画します"
      />

      {/* 設備が0件の場合 - 設備登録への誘導バナー */}
      {!isLoadingEquipments && !hasEquipments && (
        <Card
          style={{
            background: token.colorWarningBg,
            border: `1px solid ${token.colorWarningBorder}`,
          }}
        >
          <Row align="middle" gutter={16} style={{ marginBottom: 16 }}>
            <Col>
              <ToolOutlined style={{ fontSize: 32, color: '#fa8c16' }} />
            </Col>
            <Col flex={1}>
              <Typography.Title level={5} style={{ marginBottom: 4 }}>
                まず設備を登録しましょう
              </Typography.Title>
              <Typography.Text type="secondary">
                設備を登録すると、以下の機能が利用できるようになります。
              </Typography.Text>
            </Col>
            <Col>
              <Button type="primary" onClick={() => navigate('/app/equipments')}>
                設備を登録する
              </Button>
            </Col>
          </Row>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={8}>
              <Card size="small" style={{ height: '100%', background: token.colorBgElevated }}>
                <Space>
                  <LineChartOutlined style={{ fontSize: 20, color: token.colorSuccess }} />
                  <div>
                    <Typography.Text strong>健全度予測</Typography.Text>
                    <br />
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      設備の劣化状況を予測・把握
                    </Typography.Text>
                  </div>
                </Space>
              </Card>
            </Col>
            <Col xs={24} sm={8}>
              <Card size="small" style={{ height: '100%', background: token.colorBgElevated }}>
                <Space>
                  <ScheduleOutlined style={{ fontSize: 20, color: token.colorPrimary }} />
                  <div>
                    <Typography.Text strong>保全計画</Typography.Text>
                    <br />
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      最適な保全タイミングを計画
                    </Typography.Text>
                  </div>
                </Space>
              </Card>
            </Col>
            <Col xs={24} sm={8}>
              <Card size="small" style={{ height: '100%', background: token.colorBgElevated }}>
                <Space>
                  <AlertOutlined style={{ fontSize: 20, color: token.colorError }} />
                  <div>
                    <Typography.Text strong>リスク管理</Typography.Text>
                    <br />
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      故障リスクを事前に検知
                    </Typography.Text>
                  </div>
                </Space>
              </Card>
            </Col>
          </Row>
        </Card>
      )}

      {isLoading && (
        <Alert message="読み込み中" type="info" showIcon />
      )}
      {error && (
        <Alert
          message="エラー"
          description={getErrorMessage(error, "健全度データの取得に失敗しました")}
          type="error"
          showIcon
        />
      )}

      {predictionDataMissingCount > 0 && (
        <Alert
          message={`${PREDICTION_DATA_REQUIRED_MESSAGE}（${predictionDataMissingCount}件）`}
          description="入力値が不足しているため、推奨対応を算出できない設備があります。"
          type="warning"
          showIcon
          closable
        />
      )}

      <Alert
        message="健全度と残存耐用年数の見方"
        description={
          <Space direction="vertical" size={4}>
            <Typography.Text>
              健全度は、同じ条件で使う設備群のうち「まだ故障していない割合」の目安です。
            </Typography.Text>
            <Typography.Text>
              残存耐用年数0年は、交換検討ラインに達した目安であり、直ちに故障する意味ではありません。
            </Typography.Text>
            <Typography.Text>
              健全度70%以下は交換更新の検討、50%以下は故障リスクが高い状態として優先対応を検討してください。
            </Typography.Text>
          </Space>
        }
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />

      {criticalCount > 0 && (
        <Alert
          message={
            <span
              style={{ cursor: 'pointer' }}
              onClick={() => setSelectedDeteriorationRate('50below')}
            >
              健全度50%以下の設備が{criticalCount}件あります（クリックでフィルター）
            </span>
          }
          description="健全度が低下している設備です。早急なメンテナンスを検討してください。"
          type="error"
          icon={<WarningOutlined />}
          showIcon
          closable
        />
      )}

      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="平均健全度"
              value={averageHealthIndex}
              suffix="%"
              valueStyle={{ color: getHealthColor(averageHealthIndex) }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card
            style={{ cursor: 'pointer' }}
            onClick={() => setSelectedDeteriorationRate('50below')}
          >
            <Statistic
              title="健全度50%以下"
              value={criticalCount}
              valueStyle={{ color: '#cf1322' }}
              prefix={<WarningOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card
            style={{ cursor: 'pointer' }}
            onClick={() => setSelectedDeteriorationRate('70below')}
          >
            <Statistic
              title="健全度70%以下"
              value={belowSeventyCount}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="監視対象設備"
              value={filteredData.length}
            />
          </Card>
        </Col>
      </Row>

      <Card>
        <Row gutter={[16, 16]} align="middle">
          <Col flex="auto">
            <Space size="middle" wrap>
              <Select
                style={{ width: 150 }}
                placeholder="エリア"
                value={selectedLocation}
                onChange={setSelectedLocation}
              >
                <Select.Option value="all">すべて</Select.Option>
                {locationOptions.map((option) => (
                  <Select.Option key={option} value={option}>
                    {option}
                  </Select.Option>
                ))}
              </Select>
              <Select
                style={{ width: 150 }}
                placeholder="カテゴリー"
                value={selectedCategory}
                onChange={setSelectedCategory}
              >
                <Select.Option value="all">すべて</Select.Option>
                {categoryOptions.map((option) => (
                  <Select.Option key={option} value={option}>
                    {option}
                  </Select.Option>
                ))}
              </Select>
              <Select
                style={{ width: 150 }}
                placeholder="重要度"
                value={selectedRiskLevel}
                onChange={setSelectedRiskLevel}
              >
                <Select.Option value="all">すべて</Select.Option>
                <Select.Option value="critical">最重要</Select.Option>
                <Select.Option value="high">高</Select.Option>
                <Select.Option value="medium">中</Select.Option>
                <Select.Option value="low">低</Select.Option>
              </Select>
              <Select
                style={{ width: 150 }}
                placeholder="健全度"
                value={selectedDeteriorationRate}
                onChange={(value) => setSelectedDeteriorationRate(normalizeHealthFilter(value))}
              >
                <Select.Option value="all">すべて</Select.Option>
                <Select.Option value="excellent">70-100%</Select.Option>
                <Select.Option value="good">50-70%</Select.Option>
                <Select.Option value="warning">30-50%</Select.Option>
                <Select.Option value="critical">0-30%</Select.Option>
                <Select.Option value="70below">70%以下</Select.Option>
                <Select.Option value="50below">50%以下</Select.Option>
              </Select>
              <div>
                <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
                  並び替え項目
                </Typography.Text>
                <Select
                  style={{ width: 150 }}
                  value={sortPreference.field}
                  onChange={(value) =>
                    setSortPreference((current) => ({
                      ...current,
                      field: value as DeteriorationListSortField,
                    }))
                  }
                  data-testid="deterioration-sort-field-select"
                >
                  <Select.Option value="equipmentName">設備名</Select.Option>
                  <Select.Option value="health">健全度</Select.Option>
                  <Select.Option value="importanceLevel">重要度</Select.Option>
                </Select>
              </div>
              <div>
                <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
                  並び順
                </Typography.Text>
                <Select
                  style={{ width: 150 }}
                  value={sortPreference.order}
                  onChange={(value) =>
                    setSortPreference((current) => ({
                      ...current,
                      order: value as SortPreference<DeteriorationListSortField>['order'],
                    }))
                  }
                  data-testid="deterioration-sort-order-select"
                >
                  <Select.Option value="asc">{getSortOrderLabel(sortPreference.field, 'asc')}</Select.Option>
                  <Select.Option value="desc">{getSortOrderLabel(sortPreference.field, 'desc')}</Select.Option>
                </Select>
              </div>
              <RangePicker placeholder={['開始日', '終了日']} />
            </Space>
          </Col>
          <Col>
            <Space>
              <Button icon={<ReloadOutlined />} onClick={handleRefresh}>
                再計算
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      <Card>
        <Table
          columns={columns}
          dataSource={sortedData}
          rowKey="id"
          loading={isLoading}
          scroll={{ x: 1500 }}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            showSizeChanger: true,
            showTotal: (total) => `全 ${total} 件`,
            position: ['topRight', 'bottomRight'],
            onChange: (page, newPageSize) => {
              setCurrentPage(page);
              if (newPageSize && newPageSize !== pageSize) {
                setPageSize(newPageSize);
                setCurrentPage(1);
              }
            },
            onShowSizeChange: (current, size) => {
              void current;
              setPageSize(size);
              setCurrentPage(1);
            },
          }}
          rowClassName={(record) => {
            if (record.importanceLevel === 4) return 'ant-table-row-critical';
            if (record.importanceLevel === 3) return 'ant-table-row-high';
            return '';
          }}
        />
      </Card>

      <style>{`
        .ant-table-row-critical {
          background-color: ${token.colorErrorBg};
        }
        .ant-table-row-high {
          background-color: ${token.colorWarningBg};
        }
      `}</style>
    </PageContainer>
  );
}












